import { useState } from 'react';
import { Dialog, IconButton, Menu } from '@elementary/common-ui/components';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/20x20/edit.svg';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { ReactComponent as MoreHorizIcon } from '@elementary/common-ui/assets/more.svg';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { useNavigate } from 'react-router-dom';
import { RUNTIMES_BASE_PATH } from '../constants';
import RuntimeNameDialog from '../RuntimeNameDialog';
import { Runtime } from '../types';
import { toUpdateRuntimeRequest } from '../utils';
import useRuntime from '../hooks/useRuntime';

type RuntimeAction = 'rename' | 'delete';

const menuOptions = [
  { label: 'Rename', value: 'rename', icon: <EditIcon /> },
  { label: 'Delete', value: 'delete', icon: <DeleteIcon /> },
];

const isRuntimeAction = (value: string): value is RuntimeAction =>
  value === 'rename' || value === 'delete';

type Props = {
  runtime: Runtime;
};

const RuntimeActionsMenu = ({ runtime }: Props) => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState<RuntimeAction | null>(null);
  const { updateRuntime, isUpdating, deleteRuntime, isDeleting } = useRuntime(
    runtime.id,
  );

  const warehouseCount = pluralizeString(
    runtime.connections.length,
    'warehouse',
    'warehouses',
  );

  return (
    <>
      <Menu
        menuButton={
          <IconButton aria-label="Runtime actions">
            <MoreHorizIcon />
          </IconButton>
        }
        options={menuOptions}
        onChange={(value: string) => {
          if (isRuntimeAction(value)) {
            setOpenDialog(value);
          }
        }}
        closeOnChange
      />

      <RuntimeNameDialog
        isOpen={openDialog === 'rename'}
        onClose={() => setOpenDialog(null)}
        title="Rename runtime"
        confirmText="Save"
        initialName={runtime.name}
        isLoading={isUpdating}
        onSubmit={(name) =>
          updateRuntime({ ...toUpdateRuntimeRequest(runtime), name })
        }
      />

      <Dialog
        open={openDialog === 'delete'}
        onClose={() => setOpenDialog(null)}
        onConfirmButtonClick={async () => {
          await deleteRuntime();
          navigate(RUNTIMES_BASE_PATH);
        }}
        title="Delete runtime"
        message={`Deleting "${runtime.name}" revokes all of its tokens and frees its ${warehouseCount}. Runtime instances will stop connecting to Elementary right away. This can't be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
        confirmDisabled={isDeleting}
        variant="error"
      />
    </>
  );
};

export default RuntimeActionsMenu;
